import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useAuth } from "../contexts/AuthContext";
import PremiumModal from "../components/Billing/PremiumModal";
import { siteConfig } from "../config/siteConfig";

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within AppProvider");
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [view, setView] = useState("dashboard");
  const [currentExam, setCurrentExam] = useState(null);
  const [isChatOpen, _setIsChatOpen] = useState(false);
  const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);

  const { profile, isLoadingProfile, updateProfile } = useAuth();
  const isPremium = profile?.is_premium === true;

  // Tutor IA = funcion premium (alineado con la copy del pricing):
  // abrir el chat sin premium muestra el PremiumModal en su lugar.
  const setIsChatOpen = useCallback(
    (open) => {
      if (open && !isPremium) {
        setIsPremiumModalOpen(true);
        return;
      }
      _setIsChatOpen(open);
    },
    [isPremium]
  );

  // TEMARIO chargé dynamiquement et UNIQUEMENT pour un utilisateur connecté :
  // il sort ainsi du bundle de la landing (CWV/LCP) — la landing ne le télécharge jamais.
  const [temario, setOsaamisalueet] = useState([]);
  useEffect(() => {
    if (!profile) return undefined; // pas de temario sur la landing / hors session
    let alive = true;
    import("../constants/temario").then((m) => {
      if (alive) setOsaamisalueet(m.TEMARIO || []);
    });
    return () => {
      alive = false;
    };
  }, [profile]);

  const navigateToProtectedFeature = useCallback(
    (targetView, examData = null) => {
      const protectedViews = ["exam", "practice"];
      if (protectedViews.includes(targetView) && !isPremium) {
        setIsPremiumModalOpen(true);
        return;
      }
      if (examData) setCurrentExam(examData);
      setView(targetView);
    },
    [isPremium]
  );

  const [aiChatMessages, setAiChatMessages] = useState([
    {
      id: "1",
      text: siteConfig.ai.welcomeMessage,
      isUser: false,
      timestamp: new Date(),
    },
  ]);

  const calculateOverallProgress = useCallback(() => {
    const progressData = profile?.progress || {};
    if (!temario || temario.length === 0) return 0;
    const total = temario.reduce(
      (acc, topic) => acc + (progressData[topic.id] || 0),
      0
    );
    return Math.round(total / temario.length);
  }, [profile, temario]);

  const markTopicAsRead = useCallback(
    async (topicId) => {
      if (!profile) return;
      const currentRead = Array.isArray(profile.read_laws)
        ? profile.read_laws
        : [];
      let updatedLaws;

      if (currentRead.includes(topicId)) {
        updatedLaws = currentRead.filter((id) => id !== topicId);
      } else {
        updatedLaws = [...currentRead, topicId];
      }

      try {
        await updateProfile({ read_laws: updatedLaws });
      } catch (error) {
        console.error("[App] Failed to update read state:", error);
      }
    },
    [profile, updateProfile]
  );

  const updateTopicProgress = useCallback(
    async (topicId, score) => {
      if (!profile || !isPremium) return;
      const currentProgress = profile?.progress || {};
      const oldScore = currentProgress[topicId] || 0;

      if (score > oldScore) {
        const updatedProgress = { ...currentProgress, [topicId]: score };
        await updateProfile({ progress: updatedProgress });
      }
    },
    [profile, isPremium, updateProfile]
  );

  const addExamResult = useCallback(
    async (score, totalQuestions) => {
      if (!profile || !isPremium) return;
      if (!totalQuestions || totalQuestions <= 0) return;
      const rawPercentage = Math.round((score / totalQuestions) * 100);
      const percentage = Math.min(100, Math.max(0, rawPercentage));
      const newExamsCount = (profile?.exam_count || 0) + 1;
      const newBestScore = Math.max(percentage, profile?.best_score || 0);

      await updateProfile({
        exam_count: newExamsCount,
        best_score: newBestScore,
      });
    },
    [profile, isPremium, updateProfile]
  );

  const addAIMessage = useCallback((text, isUser = false, customId = null) => {
    const newMessage = {
      id: customId || Date.now().toString(),
      text,
      isUser,
      timestamp: new Date(),
    };
    setAiChatMessages((prev) => [...prev, newMessage]);
  }, []);

  useEffect(() => {
    if (view !== "exam") {
      setCurrentExam(null);
    }
  }, [view]);

  useEffect(() => {
    if (currentExam?.questions) {
      const questionsWithAnswered = currentExam.questions.map((q) => ({
        ...q,
        answered: q.answered || false,
        selectedAnswer: q.selectedAnswer || null,
      }));

      if (
        JSON.stringify(questionsWithAnswered) !==
        JSON.stringify(currentExam.questions)
      ) {
        setCurrentExam((prev) => ({
          ...prev,
          questions: questionsWithAnswered,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentExam?.sourceId]);

  const value = useMemo(
    () => ({
      view,
      setView,
      navigateToProtectedFeature,
      profile,
      isPremium,
      isLoadingProfile,
      currentExam,
      setCurrentExam,
      isChatOpen,
      setIsChatOpen,
      aiChatMessages,
      setAiChatMessages,
      calculateOverallProgress,
      markTopicAsRead,
      updateTopicProgress,
      addExamResult,
      addAIMessage,
    }),
    [
      view,
      profile,
      isPremium,
      isLoadingProfile,
      currentExam,
      isChatOpen,
      setIsChatOpen,
      aiChatMessages,
      navigateToProtectedFeature,
      calculateOverallProgress,
      markTopicAsRead,
      updateTopicProgress,
      addExamResult,
      addAIMessage,
    ]
  );

  return (
    <AppContext.Provider value={value}>
      {children}

      <PremiumModal
        isOpen={isPremiumModalOpen}
        onClose={() => setIsPremiumModalOpen(false)}
        onGoToBilling={() => {
          setIsPremiumModalOpen(false);
          setView("billing");
        }}
      />
    </AppContext.Provider>
  );
};
